import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Users, 
  Key, 
  ShieldCheck, 
  FileText, 
  Zap, 
  Check, 
  ArrowRight, 
  Download, 
  Plus, 
  Trash2, 
  Copy, 
  RefreshCw, 
  ExternalLink, 
  Sparkles, 
  Activity, 
  Sliders, 
  Building2, 
  Mail, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  Globe, 
  Layers, 
  Eye, 
  Lock,
  ChevronRight,
  TrendingUp,
  Server,
  Code2,
  Send,
  HelpCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { db, doc, setDoc, serverTimestamp } from '../firebase';
import { useAuth } from '../AuthContext';
import { useSavedSolicitations } from '../SavedSolicitationsContext';
import { Card, Button, Badge, cn } from './SaaSUIHelper';

interface SaaSManagementViewProps {
  initialTab?: 'billing' | 'team' | 'api' | 'branding' | 'audit';
}

export const SaaSManagementView: React.FC<SaaSManagementViewProps> = ({ initialTab = 'billing' }) => {
  const { user, vendorProfile, activeProfileId } = useAuth();
  const { addToast } = useSavedSolicitations();
  
  const [activeTab, setActiveTab] = useState<'billing' | 'team' | 'api' | 'branding' | 'audit'>(initialTab);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('annual');
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState<string | null>(null);
  
  // Team Management States
  const [teamMembers, setTeamMembers] = useState<Array<{
    id: string;
    name: string;
    email: string;
    role: 'Admin' | 'Proposal Manager' | 'Capture Drafter' | 'Compliance Auditor' | 'Viewer';
    status: 'Active' | 'Invited' | 'Suspended';
    joinedDate: string;
    avatarUrl?: string;
  }>>([
    {
      id: 'usr-1',
      name: vendorProfile?.companyName ? `${vendorProfile.companyName} Lead` : 'Primary Admin',
      email: user?.email || 'admin@contractor.com',
      role: 'Admin',
      status: 'Active',
      joinedDate: 'Jan 15, 2026'
    },
    {
      id: 'usr-2',
      name: 'Sarah Chen',
      email: 's.chen@contractor.com',
      role: 'Proposal Manager',
      status: 'Active',
      joinedDate: 'Feb 02, 2026'
    },
    {
      id: 'usr-3',
      name: 'Marcus Vance',
      email: 'm.vance@contractor.com',
      role: 'Capture Drafter',
      status: 'Active',
      joinedDate: 'Feb 18, 2026'
    },
    {
      id: 'usr-4',
      name: 'Elena Rostova',
      email: 'e.rostova@contractor.com',
      role: 'Compliance Auditor',
      status: 'Invited',
      joinedDate: 'Aug 20, 2026'
    }
  ]);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    name: '',
    email: '',
    role: 'Capture Drafter' as const
  });

  // API Key States
  const [apiKeys, setApiKeys] = useState<Array<{
    id: string;
    name: string;
    prefix: string;
    created: string;
    lastUsed: string;
    scopes: string[];
  }>>([
    {
      id: 'key-live-1',
      name: 'GovWin CRM Integration Key',
      prefix: 'rfx_live_9a8f...',
      created: '2026-03-12',
      lastUsed: 'Just now',
      scopes: ['solicitations:read', 'bids:read', 'crawlers:trigger']
    },
    {
      id: 'key-live-2',
      name: 'Zapier Automation Webhook Key',
      prefix: 'rfx_live_3e1b...',
      created: '2026-06-01',
      lastUsed: '2 hours ago',
      scopes: ['bids:write', 'reminders:write']
    }
  ]);

  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKeyValue, setGeneratedKeyValue] = useState<string | null>(null);

  // Webhooks States
  const [webhookUrl, setWebhookUrl] = useState('https://api.yourcompany.com/webhooks/rfxpress');
  const [webhookEvents, setWebhookEvents] = useState<string[]>([
    'solicitation.new_match',
    'bid.deadline_48h',
    'proposal.completed'
  ]);
  const [webhookTestStatus, setWebhookTestStatus] = useState<'idle' | 'testing' | 'success'>('idle');

  // White-Label / Proposal Branding States
  const [brandingConfig, setBrandingConfig] = useState({
    proposalHeader: vendorProfile?.companyName ? `${vendorProfile.companyName} Confidential Proposal` : 'Confidential Government Proposal',
    signatoryName: 'Authorized Procurement Officer',
    signatoryTitle: 'Vice President of Public Sector',
    primaryColor: '#4F46E5',
    accentColor: '#10B981',
    showComplianceWatermark: true,
    customLegalDisclaimer: 'Proprietary Proposal Response submitted under State FOIA Trade Secret Exemptions.'
  });

  // Invoice Receipt Modal State
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);

  // Current Active Tier
  const currentTier = vendorProfile?.subscriptionTier || 'pro';

  // SaaS Plans Definitions
  const plans = [
    {
      id: 'starter',
      name: 'Starter Tier',
      tagline: 'Essential portal tracking for single-state subcontractors.',
      monthlyPrice: 49,
      annualPrice: 39,
      popular: false,
      features: [
        '5 State procurement portals monitored',
        '10 AI Proposal drafts per month',
        '1 Corporate vendor profile',
        'Email deadline alerts',
        'Community support (48h SLA)',
        '1 Team seat included'
      ],
      limits: {
        proposals: 10,
        crawlers: 5,
        storage: '5 GB',
        seats: 1
      }
    },
    {
      id: 'pro',
      name: 'Professional Tier',
      tagline: 'Complete automated bidding engine for growth contractors.',
      monthlyPrice: 149,
      annualPrice: 119,
      popular: true,
      badge: 'MOST POPULAR',
      features: [
        'All 50 State procurement registries & crawlers',
        'Unlimited AI Proposal drafts & Gemini 3.7 analysis',
        'Full RFX Procurement Database access',
        'Google Workspace Integration (Drive, Docs, Sheets)',
        'Interactive Win-Rate Probability telemetry',
        'Up to 5 Team seats with custom RBAC roles',
        'Priority AI inference throughput'
      ],
      limits: {
        proposals: 'Unlimited',
        crawlers: 50,
        storage: '50 GB',
        seats: 5
      }
    },
    {
      id: 'enterprise',
      name: 'Enterprise / Prime',
      tagline: 'Dedicated infrastructure for large prime contractors & defense.',
      monthlyPrice: 499,
      annualPrice: 399,
      popular: false,
      badge: 'UNLIMITED PRIME',
      features: [
        'Custom web scraper agents deployed on demand',
        'FedRAMP & SAM.gov Federal Portal Co-op live feeds',
        'Dedicated Gemini private model throughput',
        'Custom REST API & Webhook streaming connectors',
        'White-label PDF/DOCX proposal export branding',
        'Unlimited Team seats & Organization Workspaces',
        'Dedicated 24/7 Procurement Solutions Engineer'
      ],
      limits: {
        proposals: 'Unlimited + Priority',
        crawlers: 'Unlimited Custom',
        storage: '500 GB',
        seats: 'Unlimited'
      }
    }
  ];

  // Mock Invoices
  const invoices = [
    {
      id: 'INV-2026-0891',
      date: 'Aug 01, 2026',
      period: 'Aug 01, 2026 - Aug 31, 2026',
      description: 'Professional Tier - Annual Subscription (Auto-Renewed)',
      amount: '$1,428.00',
      status: 'Paid',
      paymentMethod: 'Mastercard •••• 4092'
    },
    {
      id: 'INV-2026-0742',
      date: 'Jul 01, 2026',
      period: 'Jul 01, 2026 - Jul 31, 2026',
      description: 'Professional Tier - Monthly Seat Addon (+2 Seats)',
      amount: '$78.00',
      status: 'Paid',
      paymentMethod: 'Mastercard •••• 4092'
    },
    {
      id: 'INV-2026-0619',
      date: 'Jun 01, 2026',
      period: 'Jun 01, 2026 - Jun 30, 2026',
      description: 'Professional Tier - Monthly Subscription',
      amount: '$149.00',
      status: 'Paid',
      paymentMethod: 'PayPal Express (isa.hunter@quantumaiinc.tech)'
    }
  ];

  // Audit Logs
  const auditLogs = [
    {
      id: 'log-1',
      action: 'Crawler Ingestion Executed',
      details: 'Harvested 14 active solicitations across California & Texas registries',
      user: user?.email || 'admin@quantumaiinc.solutions',
      timestamp: '10 minutes ago',
      ip: '192.0.2.45',
      type: 'crawler'
    },
    {
      id: 'log-2',
      action: 'AI Proposal Generated',
      details: 'Completed Section 3.2 Technical Methodology for TX-DIR-2026-091',
      user: 's.chen@contractor.com',
      timestamp: '1 hour ago',
      ip: '192.0.2.78',
      type: 'ai'
    },
    {
      id: 'log-3',
      action: 'API Key Created',
      details: 'Created new key "GovWin CRM Integration Key" with 3 scopes',
      user: user?.email || 'admin@quantumaiinc.solutions',
      timestamp: '3 hours ago',
      ip: '192.0.2.45',
      type: 'security'
    },
    {
      id: 'log-4',
      action: 'Google Drive Synced',
      details: 'Exported Executive Summary & Compliance Matrix to Drive Folder',
      user: 'm.vance@contractor.com',
      timestamp: 'Yesterday at 4:15 PM',
      ip: '192.0.2.91',
      type: 'workspace'
    },
    {
      id: 'log-5',
      action: 'Team Member Invited',
      details: 'Invited Elena Rostova (e.rostova@contractor.com) as Compliance Auditor',
      user: user?.email || 'admin@quantumaiinc.solutions',
      timestamp: 'Aug 20, 2026',
      ip: '192.0.2.45',
      type: 'team'
    }
  ];

  const handlePlanUpgrade = async (planId: string) => {
    setSelectedUpgradePlan(planId);
    setIsUpgrading(true);

    try {
      if (user) {
        const docRef = doc(db, 'vendors', user.uid);
        await setDoc(docRef, {
          uid: user.uid,
          subscriptionTier: planId,
          paymentStatus: 'active',
          billingInterval: billingInterval,
          updatedAt: serverTimestamp()
        }, { merge: true });

        addToast({
          title: 'SaaS Plan Updated!',
          message: `Your account is now upgraded to ${planId.toUpperCase()} with ${billingInterval} billing. All features are instantly unlocked!`,
          type: 'success'
        });
      }
    } catch (err: any) {
      console.error('Plan upgrade error:', err);
      addToast({
        title: 'Upgrade Notice',
        message: 'Plan change recorded in your active workspace session.',
        type: 'info'
      });
    } finally {
      setIsUpgrading(false);
      setSelectedUpgradePlan(null);
    }
  };

  const handleAddTeamMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteData.name || !inviteData.email) return;

    const newMember = {
      id: `usr-${Date.now()}`,
      name: inviteData.name,
      email: inviteData.email,
      role: inviteData.role,
      status: 'Invited' as const,
      joinedDate: 'Just now'
    };

    setTeamMembers(prev => [...prev, newMember]);
    setShowInviteModal(false);
    setInviteData({ name: '', email: '', role: 'Capture Drafter' });

    addToast({
      title: 'Invitation Dispatched!',
      message: `Sent invitation link to ${inviteData.email} with ${inviteData.role} permissions.`,
      type: 'success'
    });
  };

  const handleRemoveTeamMember = (id: string, name: string) => {
    setTeamMembers(prev => prev.filter(m => m.id !== id));
    addToast({
      title: 'Member Removed',
      message: `${name} has been removed from this workspace organization.`,
      type: 'info'
    });
  };

  const handleCreateApiKey = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName) return;

    const randomSuffix = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
    const fullKey = `rfx_live_${randomSuffix}`;

    const newKeyEntry = {
      id: `key-${Date.now()}`,
      name: newKeyName,
      prefix: `rfx_live_${randomSuffix.substring(0, 4)}...`,
      created: new Date().toISOString().split('T')[0],
      lastUsed: 'Never',
      scopes: ['solicitations:read', 'bids:read', 'bids:write']
    };

    setApiKeys(prev => [...prev, newKeyEntry]);
    setGeneratedKeyValue(fullKey);
    setNewKeyName('');
  };

  const handleTestWebhook = () => {
    setWebhookTestStatus('testing');
    setTimeout(() => {
      setWebhookTestStatus('success');
      addToast({
        title: 'Webhook Ping Delivered (200 OK)',
        message: 'Endpoint acknowledged mock payload `solicitation.new_match` in 142ms.',
        type: 'success'
      });
      setTimeout(() => setWebhookTestStatus('idle'), 4000);
    }, 1200);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    addToast({
      title: 'Copied to Clipboard',
      message: `${label} copied successfully.`,
      type: 'info'
    });
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16 animate-fade-in" id="saas-management-hub">
      {/* Top Header & Breadcrumb */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xs flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100">
              SaaS Administration Hub
            </span>
            <span className="text-xs text-gray-400">•</span>
            <span className="text-xs font-semibold text-gray-500">
              Organization: <strong className="text-gray-900">{vendorProfile?.companyName || 'QuantumAI Inc.'}</strong>
            </span>
          </div>
          <h1 className="text-2xl font-black text-gray-950 tracking-tight flex items-center gap-2.5">
            Subscription, Team &amp; API Management
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl leading-relaxed">
            Manage your procurement workspace subscription tiers, multi-seat team permissions, developer webhook endpoints, and white-label proposal assets.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-gray-400 font-medium">Active Membership</p>
            <p className="text-sm font-extrabold text-gray-900 capitalize flex items-center justify-end gap-1.5">
              <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
              {currentTier} Plan
            </p>
          </div>
          <Button 
            onClick={() => setActiveTab('billing')}
            className="font-bold text-xs bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-100"
          >
            Manage Subscription
          </Button>
        </div>
      </div>

      {/* Main SaaS Navigation Tabs */}
      <div className="flex border-b border-gray-200 bg-white rounded-xl px-3 py-1.5 shadow-2xs gap-1 overflow-x-auto">
        <button
          onClick={() => setActiveTab('billing')}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap',
            activeTab === 'billing' 
              ? 'bg-indigo-50 text-indigo-700 shadow-2xs' 
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          )}
        >
          <CreditCard className="w-4 h-4" />
          <span>Plans &amp; Usage Metering</span>
        </button>

        <button
          onClick={() => setActiveTab('team')}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap',
            activeTab === 'team' 
              ? 'bg-indigo-50 text-indigo-700 shadow-2xs' 
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          )}
        >
          <Users className="w-4 h-4" />
          <span>Team &amp; Seat Access (RBAC)</span>
          <span className="bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.2 rounded-full font-extrabold">{teamMembers.length}</span>
        </button>

        <button
          onClick={() => setActiveTab('api')}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap',
            activeTab === 'api' 
              ? 'bg-indigo-50 text-indigo-700 shadow-2xs' 
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          )}
        >
          <Code2 className="w-4 h-4" />
          <span>Developer API &amp; Webhooks</span>
        </button>

        <button
          onClick={() => setActiveTab('branding')}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap',
            activeTab === 'branding' 
              ? 'bg-indigo-50 text-indigo-700 shadow-2xs' 
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          )}
        >
          <Sliders className="w-4 h-4" />
          <span>Proposal White-Labeling</span>
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap',
            activeTab === 'audit' 
              ? 'bg-indigo-50 text-indigo-700 shadow-2xs' 
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          )}
        >
          <Activity className="w-4 h-4" />
          <span>Compliance &amp; Audit Trail</span>
        </button>
      </div>

      {/* TAB 1: SUBSCRIPTION, PRICING & USAGE METERING */}
      {activeTab === 'billing' && (
        <div className="space-y-6">
          {/* Live Usage Metering Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4.5 border-indigo-100 bg-linear-to-br from-white to-indigo-50/30">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">AI Proposal Drafts</span>
                <span className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><Sparkles className="w-4 h-4" /></span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-black text-gray-900">24</span>
                <span className="text-xs font-semibold text-indigo-600">/ Unlimited</span>
              </div>
              <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3 overflow-hidden">
                <div className="bg-indigo-600 h-full rounded-full" style={{ width: '100%' }} />
              </div>
              <p className="text-[10px] text-gray-400 mt-2 font-medium">Gemini 3.7 Flash High-Throughput</p>
            </Card>

            <Card className="p-4.5 border-emerald-100 bg-linear-to-br from-white to-emerald-50/30">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Active State Crawlers</span>
                <span className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Globe className="w-4 h-4" /></span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-black text-gray-900">50</span>
                <span className="text-xs font-semibold text-emerald-600">/ 50 States</span>
              </div>
              <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3 overflow-hidden">
                <div className="bg-emerald-500 h-full rounded-full" style={{ width: '100%' }} />
              </div>
              <p className="text-[10px] text-gray-400 mt-2 font-medium">Daily Automated Scraping Active</p>
            </Card>

            <Card className="p-4.5 border-sky-100 bg-linear-to-br from-white to-sky-50/30">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Vault Cloud Storage</span>
                <span className="p-2 bg-sky-50 text-sky-600 rounded-lg"><Server className="w-4 h-4" /></span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-black text-gray-900">1.8 GB</span>
                <span className="text-xs font-semibold text-gray-500">/ 50 GB</span>
              </div>
              <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3 overflow-hidden">
                <div className="bg-sky-500 h-full rounded-full" style={{ width: '4%' }} />
              </div>
              <p className="text-[10px] text-gray-400 mt-2 font-medium">PDFs, Specs &amp; Past Performance</p>
            </Card>

            <Card className="p-4.5 border-purple-100 bg-linear-to-br from-white to-purple-50/30">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Team Member Seats</span>
                <span className="p-2 bg-purple-50 text-purple-600 rounded-lg"><Users className="w-4 h-4" /></span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-black text-gray-900">{teamMembers.length}</span>
                <span className="text-xs font-semibold text-purple-600">/ 5 Allocated</span>
              </div>
              <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3 overflow-hidden">
                <div className="bg-purple-600 h-full rounded-full" style={{ width: `${(teamMembers.length / 5) * 100}%` }} />
              </div>
              <p className="text-[10px] text-gray-400 mt-2 font-medium">RBAC Security Active</p>
            </Card>
          </div>

          {/* Pricing Tiers Selection with Interval Switcher */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-5">
              <div>
                <h2 className="text-lg font-black text-gray-900 tracking-tight">Select Procurement SaaS Plan</h2>
                <p className="text-xs text-gray-500 mt-0.5">Upgrade or adjust your subscription anytime. Instant capability unlock across all workspaces.</p>
              </div>

              {/* Monthly / Annual Toggle with 20% discount */}
              <div className="flex items-center p-1 bg-gray-100 rounded-xl border border-gray-200 shrink-0">
                <button
                  onClick={() => setBillingInterval('monthly')}
                  className={cn(
                    'px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer',
                    billingInterval === 'monthly' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                  )}
                >
                  Monthly Billing
                </button>
                <button
                  onClick={() => setBillingInterval('annual')}
                  className={cn(
                    'px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5',
                    billingInterval === 'annual' ? 'bg-indigo-600 text-white shadow-xs' : 'text-gray-500 hover:text-gray-900'
                  )}
                >
                  <span>Annual Billing</span>
                  <span className={cn(
                    'text-[9px] px-1.5 py-0.2 rounded-full font-black uppercase',
                    billingInterval === 'annual' ? 'bg-indigo-800 text-indigo-200' : 'bg-emerald-100 text-emerald-700'
                  )}>
                    Save 20%
                  </span>
                </button>
              </div>
            </div>

            {/* Plan Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((p) => {
                const isCurrent = currentTier === p.id;
                const price = billingInterval === 'annual' ? p.annualPrice : p.monthlyPrice;

                return (
                  <div
                    key={p.id}
                    className={cn(
                      'rounded-2xl border p-6 flex flex-col justify-between transition-all duration-200 relative',
                      p.popular 
                        ? 'border-indigo-600 bg-linear-to-b from-indigo-50/30 to-white shadow-lg ring-2 ring-indigo-600/10' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    )}
                  >
                    {p.badge && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-wider px-3 py-0.5 rounded-full shadow-sm">
                        {p.badge}
                      </div>
                    )}

                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between">
                          <h3 className="text-base font-black text-gray-900">{p.name}</h3>
                          {isCurrent && (
                            <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                              Active Plan
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 min-h-[32px]">{p.tagline}</p>
                      </div>

                      <div className="flex items-baseline gap-1 pt-1">
                        <span className="text-3xl font-black text-gray-950">${price}</span>
                        <span className="text-xs text-gray-500 font-medium">/ user / month</span>
                      </div>
                      {billingInterval === 'annual' && (
                        <p className="text-[10px] text-emerald-600 font-bold">Billed annually (${price * 12}/yr)</p>
                      )}

                      <div className="border-t border-gray-150 pt-4 space-y-2.5">
                        <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Features Included:</p>
                        <ul className="space-y-2">
                          {p.features.map((feat, fi) => (
                            <li key={fi} className="flex items-start gap-2 text-xs text-gray-600">
                              <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0 mt-0.5" />
                              <span className="leading-tight">{feat}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="pt-6">
                      <Button
                        onClick={() => handlePlanUpgrade(p.id)}
                        disabled={isCurrent || isUpgrading}
                        variant={p.popular ? 'primary' : 'outline'}
                        className={cn(
                          'w-full font-bold text-xs h-10',
                          p.popular ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'border-gray-300'
                        )}
                      >
                        {isUpgrading && selectedUpgradePlan === p.id ? (
                          <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Upgrading...
                          </div>
                        ) : isCurrent ? (
                          'Current Active Tier'
                        ) : (
                          `Switch to ${p.name}`
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment Method & Invoices Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Payment Method Card */}
            <Card className="p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-indigo-600" />
                  Primary Payment Method
                </h3>
                <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-full">
                  Verified
                </span>
              </div>

              <div className="bg-slate-900 text-white rounded-xl p-4 space-y-3 relative overflow-hidden">
                <div className="flex justify-between items-center relative z-10">
                  <span className="text-[10px] font-mono text-slate-400">CORPORATE PROCUREMENT CARD</span>
                  <Zap className="w-4 h-4 text-amber-400" />
                </div>
                <p className="text-base font-mono tracking-widest relative z-10">•••• •••• •••• 4092</p>
                <div className="flex justify-between items-end text-xs relative z-10">
                  <div>
                    <p className="text-[9px] text-slate-400">CARDHOLDER</p>
                    <p className="font-bold">{vendorProfile?.companyName || 'QUANTUMAI INC'}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-400">EXPIRES</p>
                    <p className="font-bold">08/29</p>
                  </div>
                </div>
              </div>

              <div className="pt-1 flex gap-2">
                <Button variant="outline" size="sm" className="w-full text-xs font-semibold">
                  Update Card
                </Button>
                <Button variant="outline" size="sm" className="w-full text-xs font-semibold">
                  Connect PayPal
                </Button>
              </div>
            </Card>

            {/* Invoices List */}
            <Card className="p-5 lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    Billing History &amp; Tax Receipts
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Download itemized PDF receipts for accounting &amp; contract expense reports.</p>
                </div>
                <Button variant="ghost" size="sm" className="text-xs text-indigo-600 font-bold">
                  Export All (CSV)
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-gray-150 text-gray-500 font-bold text-[10px] uppercase">
                      <th className="pb-2">Invoice #</th>
                      <th className="pb-2">Billing Period</th>
                      <th className="pb-2">Amount</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2 text-right">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-3 font-mono font-bold text-gray-900">{inv.id}</td>
                        <td className="py-3 text-gray-600">{inv.date}</td>
                        <td className="py-3 font-bold text-gray-900">{inv.amount}</td>
                        <td className="py-3">
                          <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
                            {inv.status}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <button
                            onClick={() => setSelectedInvoice(inv)}
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-bold text-xs cursor-pointer"
                          >
                            <Download className="w-3.5 h-3.5" /> PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* TAB 2: TEAM MEMBERS & ROLE-BASED ACCESS CONTROL (RBAC) */}
      {activeTab === 'team' && (
        <div className="space-y-6">
          {/* Header Action Bar */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-black text-gray-900 tracking-tight flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Workspace Team Members &amp; RBAC
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Manage your proposal managers, capture drafters, compliance auditors, and read-only executive stakeholders.
              </p>
            </div>

            <Button
              onClick={() => setShowInviteModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs flex items-center gap-2 shadow-md shadow-indigo-100"
            >
              <Plus className="w-4 h-4" />
              Invite Colleague
            </Button>
          </div>

          {/* Team Members Table */}
          <Card className="overflow-hidden">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-150 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  <th className="py-3 px-4">Member Name &amp; Email</th>
                  <th className="py-3 px-4">Workspace Role</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Joined Date</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teamMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs shrink-0">
                          {member.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-xs">{member.name}</p>
                          <p className="text-[11px] text-gray-500 font-mono">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={cn(
                        'text-[11px] font-bold px-2.5 py-1 rounded-md border',
                        member.role === 'Admin' && 'bg-purple-50 text-purple-700 border-purple-200',
                        member.role === 'Proposal Manager' && 'bg-indigo-50 text-indigo-700 border-indigo-200',
                        member.role === 'Capture Drafter' && 'bg-sky-50 text-sky-700 border-sky-200',
                        member.role === 'Compliance Auditor' && 'bg-amber-50 text-amber-700 border-amber-200',
                        member.role === 'Viewer' && 'bg-gray-100 text-gray-700 border-gray-200'
                      )}>
                        {member.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={cn(
                        'text-[10px] font-bold px-2 py-0.5 rounded-full',
                        member.status === 'Active' && 'bg-emerald-50 text-emerald-700 border border-emerald-200',
                        member.status === 'Invited' && 'bg-amber-50 text-amber-700 border border-amber-200 animate-pulse'
                      )}>
                        {member.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-[11px]">{member.joinedDate}</td>
                    <td className="py-3 px-4 text-right">
                      {member.role !== 'Admin' ? (
                        <button
                          onClick={() => handleRemoveTeamMember(member.id, member.name)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                          title="Remove Team Member"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-[10px] text-gray-400 font-semibold italic">Owner</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Role Permission Matrix Card */}
          <Card className="p-6 space-y-4">
            <div>
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-indigo-600" />
                SaaS Role Permission Matrix
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Summary of RBAC functional authorizations per seat tier.</p>
            </div>

            <div className="overflow-x-auto border border-gray-150 rounded-xl">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-150 text-[10px] font-bold text-gray-500 uppercase">
                    <th className="py-2.5 px-3">Permission Scope</th>
                    <th className="py-2.5 px-3 text-center">Admin</th>
                    <th className="py-2.5 px-3 text-center">Proposal Manager</th>
                    <th className="py-2.5 px-3 text-center">Capture Drafter</th>
                    <th className="py-2.5 px-3 text-center">Compliance Auditor</th>
                    <th className="py-2.5 px-3 text-center">Viewer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-[11px]">
                  <tr>
                    <td className="py-2.5 px-3 font-medium text-gray-800">Trigger Daily 50-State Crawlers</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 font-medium text-gray-800">Generate Gemini 3.7 AI Proposals</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 font-medium text-gray-800">Export Proposals (PDF / Google Docs)</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 font-medium text-gray-800">Manage Developer API Keys &amp; Webhooks</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 font-medium text-gray-800">Billing, Plan Upgrades &amp; Invoices</td>
                    <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">✓</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                    <td className="py-2.5 px-3 text-center text-gray-300">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 3: DEVELOPER API & WEBHOOKS */}
      {activeTab === 'api' && (
        <div className="space-y-6">
          {/* API Keys Management */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xs space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
              <div>
                <h2 className="text-lg font-black text-gray-900 tracking-tight flex items-center gap-2">
                  <Key className="w-5 h-5 text-indigo-600" />
                  REST API Keys (SaaS Integration)
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Use API keys to authenticate programmatic requests from your CRM (Salesforce, GovWin, HubSpot) or automated CI/CD pipelines.
                </p>
              </div>

              <Button
                onClick={() => setShowNewKeyModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs flex items-center gap-2 shrink-0 shadow-md shadow-indigo-100"
              >
                <Plus className="w-4 h-4" />
                Generate New API Key
              </Button>
            </div>

            {/* Keys Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-gray-150 text-[10px] font-bold text-gray-500 uppercase">
                    <th className="pb-2">Key Label</th>
                    <th className="pb-2">Key Prefix</th>
                    <th className="pb-2">Scopes</th>
                    <th className="pb-2">Created</th>
                    <th className="pb-2">Last Used</th>
                    <th className="pb-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {apiKeys.map((k) => (
                    <tr key={k.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="py-3 font-bold text-gray-900">{k.name}</td>
                      <td className="py-3 font-mono text-gray-600 bg-gray-50 px-2 py-0.5 rounded w-fit">{k.prefix}</td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1">
                          {k.scopes.map(s => (
                            <span key={s} className="text-[9px] font-mono bg-indigo-50 text-indigo-700 px-1.5 py-0.2 rounded border border-indigo-100">
                              {s}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 text-gray-500 text-[11px]">{k.created}</td>
                      <td className="py-3 text-gray-500 text-[11px]">{k.lastUsed}</td>
                      <td className="py-3 text-right">
                        <button
                          onClick={() => setApiKeys(prev => prev.filter(item => item.id !== k.id))}
                          className="text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                          title="Revoke Key"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Webhooks Configuration Card */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xs space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
              <div>
                <h3 className="text-base font-black text-gray-900 flex items-center gap-2">
                  <Send className="w-4.5 h-4.5 text-indigo-600" />
                  Real-time Webhook Streaming Subscriptions
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Receive instant HTTP POST alerts whenever fresh state opportunities match your NAICS/NIGP criteria.</p>
              </div>

              <Button
                onClick={handleTestWebhook}
                variant="outline"
                size="sm"
                disabled={webhookTestStatus === 'testing'}
                className="font-bold text-xs flex items-center gap-2 shrink-0 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
              >
                {webhookTestStatus === 'testing' ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Sending Ping...
                  </>
                ) : (
                  <>
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                    Send Test Ping (200 OK)
                  </>
                )}
              </Button>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700">Webhook Destination URL</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="https://your-domain.com/webhooks/rfxpress"
                  />
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs">
                    Save URL
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700">Subscribed Event Triggers</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { id: 'solicitation.new_match', label: 'solicitation.new_match', desc: 'Fired when a new RFX matches your vendor codes' },
                    { id: 'bid.deadline_48h', label: 'bid.deadline_48h', desc: 'Warning fired 48h before bid proposal deadline' },
                    { id: 'proposal.completed', label: 'proposal.completed', desc: 'Triggered when AI drafting engine finishes sections' }
                  ].map((evt) => (
                    <div key={evt.id} className="p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={webhookEvents.includes(evt.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setWebhookEvents(prev => [...prev, evt.id]);
                            } else {
                              setWebhookEvents(prev => prev.filter(x => x !== evt.id));
                            }
                          }}
                          className="rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                        />
                        <span className="font-mono font-bold text-xs text-gray-900">{evt.label}</span>
                      </div>
                      <p className="text-[10px] text-gray-500 pl-5 leading-tight">{evt.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Quick API Explorer / cURL Snippet */}
          <Card className="p-6 space-y-3 bg-slate-950 text-slate-200 border-slate-900">
            <div className="flex justify-between items-center">
              <span className="text-xs font-bold text-emerald-400 font-mono flex items-center gap-2">
                <Code2 className="w-4 h-4" /> cURL Example: Fetch Active State Solicitations
              </span>
              <button
                onClick={() => copyToClipboard(`curl -X GET "https://ais-dev-bu4nplkevkzgprpokeccx3-201011828773.us-west1.run.app/api/solicitations?state=CA&limit=10" \\\n  -H "Authorization: Bearer rfx_live_9a8f..." \\\n  -H "Content-Type: application/json"`, 'cURL Snippet')}
                className="text-xs text-slate-400 hover:text-white flex items-center gap-1 cursor-pointer"
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
            <pre className="font-mono text-xs text-emerald-300 bg-slate-900 p-4 rounded-xl overflow-x-auto leading-relaxed border border-slate-800">
{`curl -X GET "https://ais-dev-bu4nplkevkzgprpokeccx3-201011828773.us-west1.run.app/api/solicitations?state=CA&limit=10" \\
  -H "Authorization: Bearer rfx_live_9a8f..." \\
  -H "Content-Type: application/json"`}
            </pre>
          </Card>
        </div>
      )}

      {/* TAB 4: PROPOSAL WHITE-LABELING */}
      {activeTab === 'branding' && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xs space-y-6">
            <div className="border-b border-gray-100 pb-4">
              <h2 className="text-lg font-black text-gray-900 tracking-tight flex items-center gap-2">
                <Sliders className="w-5 h-5 text-indigo-600" />
                Corporate White-Labeling &amp; Proposal Deck Branding
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Configure authorized header footers, sign-off blocks, and corporate colors stamped onto generated AI proposals and PDF bid decks.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-700">Proposal Document Header / Title</label>
                  <input
                    type="text"
                    value={brandingConfig.proposalHeader}
                    onChange={(e) => setBrandingConfig(prev => ({ ...prev, proposalHeader: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-900 focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700">Authorized Signatory Name</label>
                    <input
                      type="text"
                      value={brandingConfig.signatoryName}
                      onChange={(e) => setBrandingConfig(prev => ({ ...prev, signatoryName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-900 focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700">Signatory Corporate Title</label>
                    <input
                      type="text"
                      value={brandingConfig.signatoryTitle}
                      onChange={(e) => setBrandingConfig(prev => ({ ...prev, signatoryTitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-900 focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-700">Legal Trade Secret Disclaimer (Footer)</label>
                  <textarea
                    rows={2}
                    value={brandingConfig.customLegalDisclaimer}
                    onChange={(e) => setBrandingConfig(prev => ({ ...prev, customLegalDisclaimer: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-900 focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>

                <Button
                  onClick={() => {
                    addToast({
                      title: 'Branding Saved',
                      message: 'Corporate white-label settings applied to all upcoming PDF proposal exports.',
                      type: 'success'
                    });
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs"
                >
                  Save White-Label Template
                </Button>
              </div>

              {/* Live Preview Card */}
              <div className="border border-gray-200 rounded-xl p-5 bg-gray-50/50 space-y-4">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Live Proposal Cover Preview</span>
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-xs space-y-4">
                  <div className="border-b border-gray-150 pb-3 flex justify-between items-center">
                    <span className="text-xs font-black text-indigo-700">{vendorProfile?.companyName || 'QUANTUMAI SOLUTIONS INC.'}</span>
                    <span className="text-[9px] font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">RFP SUBMISSION</span>
                  </div>

                  <div className="space-y-1">
                    <h4 className="text-sm font-black text-gray-900">{brandingConfig.proposalHeader}</h4>
                    <p className="text-[11px] text-gray-500">Prepared in Response to Solicitation: CDT-2026-9402</p>
                  </div>

                  <div className="pt-4 border-t border-gray-150 flex justify-between items-end text-[10px] text-gray-500">
                    <div>
                      <p className="font-bold text-gray-900">{brandingConfig.signatoryName}</p>
                      <p>{brandingConfig.signatoryTitle}</p>
                    </div>
                    <p className="text-[9px] text-gray-400 max-w-xs text-right italic">{brandingConfig.customLegalDisclaimer}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: AUDIT LOGS & COMPLIANCE */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xs space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
              <div>
                <h2 className="text-lg font-black text-gray-900 tracking-tight flex items-center gap-2">
                  <Activity className="w-5 h-5 text-indigo-600" />
                  SaaS Security &amp; Activity Audit Log
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Immutable security ledger of all crawler runs, proposal drafts, team invites, and document downloads.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                  Live SOC2 Audit Stream
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-gray-150 text-[10px] font-bold text-gray-500 uppercase">
                    <th className="pb-2">Action Event</th>
                    <th className="pb-2">Details</th>
                    <th className="pb-2">Initiated By</th>
                    <th className="pb-2">Timestamp</th>
                    <th className="pb-2">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="py-3 font-bold text-gray-900">
                        <span className="inline-flex items-center gap-1.5">
                          {log.type === 'crawler' && <Globe className="w-3.5 h-3.5 text-emerald-600" />}
                          {log.type === 'ai' && <Sparkles className="w-3.5 h-3.5 text-indigo-600" />}
                          {log.type === 'security' && <Key className="w-3.5 h-3.5 text-amber-600" />}
                          {log.type === 'workspace' && <Server className="w-3.5 h-3.5 text-sky-600" />}
                          {log.type === 'team' && <Users className="w-3.5 h-3.5 text-purple-600" />}
                          {log.action}
                        </span>
                      </td>
                      <td className="py-3 text-gray-600 max-w-md truncate">{log.details}</td>
                      <td className="py-3 text-gray-700 font-mono text-[11px]">{log.user}</td>
                      <td className="py-3 text-gray-500 text-[11px] whitespace-nowrap">{log.timestamp}</td>
                      <td className="py-3 text-gray-400 font-mono text-[11px]">{log.ip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl border border-gray-100 space-y-4"
          >
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-base font-black text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Invite Colleague to Workspace
              </h3>
              <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>

            <form onSubmit={handleAddTeamMember} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700">Full Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Rachel Adams"
                  value={inviteData.name}
                  onChange={(e) => setInviteData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700">Email Address</label>
                <input
                  type="email"
                  required
                  placeholder="r.adams@contractor.com"
                  value={inviteData.email}
                  onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700">Role &amp; Permissions</label>
                <select
                  value={inviteData.role}
                  onChange={(e) => setInviteData(prev => ({ ...prev, role: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="Proposal Manager">Proposal Manager (Full Drafts &amp; Export)</option>
                  <option value="Capture Drafter">Capture Drafter (Research &amp; Write Sections)</option>
                  <option value="Compliance Auditor">Compliance Auditor (MWBE &amp; Set-Aside Reviews)</option>
                  <option value="Viewer">Viewer (Read-Only Stakeholder)</option>
                </select>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                  Send Invitation
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Generate API Key Modal */}
      {showNewKeyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl border border-gray-100 space-y-4"
          >
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-base font-black text-gray-900 flex items-center gap-2">
                <Key className="w-5 h-5 text-indigo-600" />
                Generate New REST API Key
              </h3>
              <button onClick={() => { setShowNewKeyModal(false); setGeneratedKeyValue(null); }} className="text-gray-400 hover:text-gray-600">×</button>
            </div>

            {!generatedKeyValue ? (
              <form onSubmit={handleCreateApiKey} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700">API Key Label / Purpose</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Deltek GovWin Sync Agent"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="pt-2 flex justify-end gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowNewKeyModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                    Create Key
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-emerald-800 text-xs flex gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <p>Key generated successfully! Make sure to copy it now as you won't be able to view it again.</p>
                </div>

                <div className="bg-slate-900 p-3 rounded-xl flex items-center justify-between font-mono text-xs text-emerald-400">
                  <span className="truncate">{generatedKeyValue}</span>
                  <button
                    onClick={() => copyToClipboard(generatedKeyValue, 'API Key')}
                    className="p-1.5 hover:bg-slate-800 text-white rounded transition-colors cursor-pointer"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex justify-end pt-2">
                  <Button size="sm" onClick={() => { setShowNewKeyModal(false); setGeneratedKeyValue(null); }} className="bg-indigo-600 text-white">
                    Done
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Invoice PDF Preview Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl w-full max-w-2xl p-8 shadow-2xl border border-gray-100 space-y-6"
          >
            <div className="flex justify-between items-start border-b border-gray-200 pb-5">
              <div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
                    <Zap className="w-4 h-4" />
                  </div>
                  <span className="text-xl font-black text-gray-950">RFXpress SaaS</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Autonomous Public Procurement Platform</p>
              </div>

              <div className="text-right">
                <span className="text-xs font-mono font-bold text-gray-500 uppercase">INVOICE RECEIPT</span>
                <p className="text-base font-black text-gray-900 mt-0.5">{selectedInvoice.id}</p>
                <p className="text-xs text-gray-500">{selectedInvoice.date}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 text-xs">
              <div>
                <p className="font-bold text-gray-400 uppercase text-[10px]">BILLED TO:</p>
                <p className="font-bold text-gray-900 text-sm mt-1">{vendorProfile?.companyName || 'QuantumAI Inc.'}</p>
                <p className="text-gray-600">{user?.email || 'admin@contractor.com'}</p>
                <p className="text-gray-500 mt-1">Payment: {selectedInvoice.paymentMethod}</p>
              </div>

              <div className="text-right">
                <p className="font-bold text-gray-400 uppercase text-[10px]">STATUS:</p>
                <span className="inline-block mt-1 bg-emerald-100 text-emerald-800 font-bold px-3 py-1 rounded-full text-xs">
                  {selectedInvoice.status}
                </span>
                <p className="text-gray-500 text-[11px] mt-1.5">Period: {selectedInvoice.period}</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-bold">
                  <tr>
                    <th className="py-2.5 px-4">Item &amp; Description</th>
                    <th className="py-2.5 px-4 text-right">Rate</th>
                    <th className="py-2.5 px-4 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-900">{selectedInvoice.description}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{selectedInvoice.amount}</td>
                    <td className="py-3 px-4 text-right font-bold text-gray-900">{selectedInvoice.amount}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center pt-2">
              <p className="text-[11px] text-gray-400">Thank you for your business with RFXpress SaaS.</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedInvoice(null)}>
                  Close
                </Button>
                <Button size="sm" onClick={() => {
                  window.print();
                }} className="bg-indigo-600 text-white font-bold">
                  <Download className="w-3.5 h-3.5 mr-1" /> Print / Save PDF
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
